import { AnyAction, ThunkDispatch, createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { logger } from 'common/services';
import { isError } from 'common/utils';
import type { MediaDevice, MediaSettingsState } from 'devices/types';
import { checkAudioAccess, checkMediaAccess, getMediaDevices } from 'devices/utils';
import { filter, isEqual } from 'lodash-es';
import { addAppErrorMessage } from 'modules/alerts/state';
import type { AppDispatch, RootState } from 'store';

import { selectMediaDevices, selectMediaDevicesLoaded } from './selectors';
import { UserSettings } from './types';

type ThunkAPI = {
  dispatch: AppDispatch;
  state: RootState;
  extra: unknown;
  rejectWithValue: unknown;
};

type DevicesChangedOptions = {
  audioOnly?: boolean;
};

const devicesChangedHandler = async (
  options: DevicesChangedOptions | undefined,
  { dispatch, getState }: { dispatch: ThunkDispatch<RootState, unknown, AnyAction>; getState: () => RootState }
): Promise<void> => {
  try {
    const state = getState();
    const newDevices: MediaDevice[] = await getDevices(state, options?.audioOnly ?? false);

    const currentMediaDeviceIds = selectMediaDevices(state).map(({ deviceId }) => deviceId);
    const newDeviceIds = newDevices.map(({ deviceId }) => deviceId);
    // We do this comparison to prevent unnecessary re-renders
    if (!isEqual(currentMediaDeviceIds, newDeviceIds)) {
      dispatch(userActions.navigator.mediaDevices.devicesFound(newDevices));
    }
  } catch (error) {
    logger.error(error);

    if (isError(error)) {
      dispatch(addAppErrorMessage(error.message));
    }
  }
};

const wasMediaAccessGiven = (devices: MediaDevice[], audioOnly: boolean) => {
  const audioInputDevices = filter(devices, ['kind', 'audioinput']);

  if (audioOnly) {
    return audioInputDevices.length > 0;
  }

  const videoDevices = filter(devices, ['kind', 'videoinput']);
  return audioInputDevices.length > 0 && videoDevices.length > 0;
};

const getDevices = async (state: RootState, audioOnly: boolean): Promise<MediaDevice[]> => {
  const devices: MediaDevice[] = await getMediaDevices();
  const hasRequiredAccess = wasMediaAccessGiven(devices, audioOnly);

  if (!hasRequiredAccess) {
    const wereMediaDevicesAlreadyLoaded = selectMediaDevicesLoaded(state);

    // Check what access we currently have
    const hasAudioAccess = wasMediaAccessGiven(devices, true);
    const hasVideoAccess = wasMediaAccessGiven(devices, false);

    // Determine if we need to request additional permissions
    const isFirstTimeRequest = !wereMediaDevicesAlreadyLoaded;
    const needsCameraUpgrade = !audioOnly && hasAudioAccess && !hasVideoAccess;

    // Request access if:
    // 1. First time requesting (devices never loaded), OR
    // 2. Upgrading from audio-only to full access (user granted audio in Natalie, now needs camera for Events)
    const shouldRequestAccess = isFirstTimeRequest || needsCameraUpgrade;

    if (shouldRequestAccess) {
      const hasAccess = audioOnly ? (await checkAudioAccess()).hasAccess : await checkMediaAccess();
      if (hasAccess) {
        return getMediaDevices();
      }
    }
  }

  return devices;
};

export const userActions = {
  navigator: {
    mediaDevices: {
      devicesChanged: createAsyncThunk<void, DevicesChangedOptions | undefined, ThunkAPI>(
        '[navigator mediaDevices] devices changed',
        devicesChangedHandler
      ),
      pollForDevicesListChange: createAsyncThunk<void, DevicesChangedOptions | undefined, ThunkAPI>(
        '[navigator mediaDevices] poll for devices list change',
        devicesChangedHandler
      ),
      devicesFound: createAction<MediaDevice[]>('[navigator mediaDevices] devices found'),
    },
  },
  mediaSettings: {
    settingsUpdated: createAction<UserSettings & MediaSettingsState>('[mediaSettings] settings updated'),
  },
};
